import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { useAuthContext } from '@/contexts/AuthContext';
import { CardsResumo, MetricCard } from '@/components/dashboard/CardsResumo';
import { GraficoClientesPorStatus } from '@/components/dashboard/GraficoClientesPorStatus';
import { GraficoTempoMedio } from '@/components/dashboard/GraficoTempoMedio';
import { TabelaDesempenhoAtendentes } from '@/components/dashboard/TabelaDesempenhoAtendentes';
import { GraficoOSPorStatus } from '@/components/dashboard/GraficoOSPorStatus';
import { TabelaDesempenhoPrestadores } from '@/components/dashboard/TabelaDesempenhoPrestadores';
import { CardsResumoFinanceiro } from '@/components/dashboard/CardsResumoFinanceiro';
import { GraficoFaturamento } from '@/components/dashboard/GraficoFaturamento';
import { TabelaServicosMaisVendidos } from '@/components/dashboard/TabelaServicosMaisVendidos';
import { GraficoServicosPizza } from '@/components/dashboard/GraficoServicosPizza';
import {
  useClientesPorStatus,
  useTempoMedioAtendimento,
  useDesempenhoAtendentes,
  useOSPorStatus,
  useDesempenhoPrestadores,
} from '@/hooks/useDashboard';
import {
  useMetricasFinanceiras,
  useServicosMaisVendidos,
  useFaturamentoPorPeriodo,
} from '@/hooks/useDashboardFinanceiro';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';

type Periodo = '7d' | '30d' | '90d';
type Tab = 'clientes' | 'ordens' | 'financeiro';

interface SectionProps {
  titulo: React.ReactNode;
  descricao?: string;
  children: React.ReactNode;
  flush?: boolean;
}

function Section({ titulo, descricao, children, flush = false }: SectionProps) {
  return (
    <div className="ow-card">
      <div className="px-6 pt-5 pb-3">
        <h3 className="text-base font-bold tracking-tight">{titulo}</h3>
        {descricao && <p className="text-xs text-muted-foreground mt-0.5">{descricao}</p>}
      </div>
      <div className={flush ? '' : 'px-6 pb-5'}>{children}</div>
    </div>
  );
}

interface PillGroupProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}

function PillGroup<T extends string>({ value, options, onChange }: PillGroupProps<T>) {
  return (
    <div className="inline-flex p-1 rounded-full bg-muted/60 gap-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            value === o.value
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function Dashboard() {
  useFeatureTracking('dashboard');
  const { t } = useTranslation();
  const { isAdmin } = useAuthContext();
  const [periodoSelecionado, setPeriodoSelecionado] = useState<Periodo>('7d');
  const [activeTab, setActiveTab] = useState<Tab>('clientes');

  const { data: dadosStatus, isLoading: loadingStatus } = useClientesPorStatus();
  const { data: dadosTempo, isLoading: loadingTempo } = useTempoMedioAtendimento(periodoSelecionado);
  const { data: dadosDesempenho, isLoading: loadingDesempenho } = useDesempenhoAtendentes();
  const { data: dadosOSStatus, isLoading: loadingOSStatus } = useOSPorStatus();
  const { data: dadosPrestadores, isLoading: loadingPrestadores } = useDesempenhoPrestadores();
  const { data: metricasFinanceiras, isLoading: loadingMetricasFinanceiras } =
    useMetricasFinanceiras(periodoSelecionado);
  const { data: servicosMaisVendidos, isLoading: loadingServicos } =
    useServicosMaisVendidos(periodoSelecionado);
  const { data: faturamentoPorPeriodo, isLoading: loadingFaturamento } =
    useFaturamentoPorPeriodo(periodoSelecionado);

  const periodoLabel: Record<Periodo, string> = {
    '7d': t('dashboard.last7days'),
    '30d': t('dashboard.last30days'),
    '90d': t('dashboard.last90days'),
  };

  const totalOS = dadosOSStatus?.reduce((acc, os) => acc + os.quantidade, 0) || 0;
  const osAtivas =
    dadosOSStatus
      ?.filter((os) => ['pendente', 'aceita', 'em_execucao'].includes(os.statusKey))
      .reduce((acc, os) => acc + os.quantidade, 0) || 0;
  const osConcluidas = dadosOSStatus?.find((o) => o.statusKey === 'concluida')?.quantidade || 0;
  const osCanceladas = dadosOSStatus?.find((o) => o.statusKey === 'cancelada')?.quantidade || 0;

  const tabs: { value: Tab; label: string }[] = [
    { value: 'clientes', label: t('dashboard.tabs.clients') },
    { value: 'ordens', label: t('dashboard.tabs.orders') },
    ...(isAdmin ? [{ value: 'financeiro' as Tab, label: t('dashboard.tabs.financial') }] : []),
  ];

  return (
    <MainLayout>
      <div className="p-6 flex flex-col gap-5">
        <PageHeader
          titulo={t('dashboard.title')}
          acoes={
            <>
              <PillGroup<Periodo>
                value={periodoSelecionado}
                options={[
                  { value: '7d', label: t('dashboard.periodOptions.7d') },
                  { value: '30d', label: t('dashboard.periodOptions.30d') },
                  { value: '90d', label: t('dashboard.periodOptions.90d') },
                ]}
                onChange={setPeriodoSelecionado}
              />
              <UserMenu />
            </>
          }
        />

        {/* Pill tabs */}
        <PillGroup<Tab> value={activeTab} options={tabs} onChange={setActiveTab} />

        {/* Tab: Clientes */}
        {activeTab === 'clientes' && (
          <div className="flex flex-col gap-5">
            {loadingStatus || loadingTempo ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-28 rounded-2xl" />
                ))}
              </div>
            ) : (
              dadosStatus && dadosTempo && (
                <CardsResumo dadosStatus={dadosStatus} dadosTempo={dadosTempo} />
              )
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section
                titulo={t('dashboard.charts.clientsByStatus')}
                descricao={t('dashboard.charts.clientsByStatusDescription')}
              >
                {loadingStatus ? (
                  <div className="h-[300px] flex items-center justify-center">
                    <Skeleton className="h-[200px] w-[200px] rounded-full" />
                  </div>
                ) : dadosStatus ? (
                  <GraficoClientesPorStatus dados={dadosStatus} />
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t('common.noData')}
                  </div>
                )}
              </Section>

              <Section
                titulo={t('dashboard.charts.averageServiceTime')}
                descricao={t('dashboard.charts.averageServiceTimeDescription', {
                  period: periodoLabel[periodoSelecionado],
                })}
              >
                {loadingTempo ? (
                  <div className="space-y-4">
                    <Skeleton className="h-20" />
                    <Skeleton className="h-[200px]" />
                  </div>
                ) : dadosTempo ? (
                  <GraficoTempoMedio dados={dadosTempo} />
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t('common.noData')}
                  </div>
                )}
              </Section>
            </div>

            <Section
              titulo={isAdmin ? t('dashboard.tables.agentPerformance') : t('dashboard.tabs.clients')}
              descricao={
                isAdmin
                  ? t('dashboard.tables.agentPerformanceDescription')
                  : t('dashboard.subtitleAgent')
              }
            >
              {loadingDesempenho ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-14" />
                  ))}
                </div>
              ) : dadosDesempenho && dadosDesempenho.length > 0 ? (
                <TabelaDesempenhoAtendentes dados={dadosDesempenho} />
              ) : (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  {t('common.noData')}
                </div>
              )}
            </Section>
          </div>
        )}

        {/* Tab: Ordens */}
        {activeTab === 'ordens' && (
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard titulo={t('dashboard.cards.totalOS')} valor={totalOS} />
              <MetricCard titulo={t('dashboard.cards.activeOS')} valor={osAtivas} />
              <MetricCard
                titulo={t('dashboard.cards.completedOS')}
                valor={osConcluidas}
              />
              <MetricCard
                titulo={t('orders.status.cancelled')}
                valor={osCanceladas}
              />
            </div>

            <Section
              titulo={t('dashboard.charts.osByStatus')}
              descricao={t('dashboard.charts.osByStatusDescription')}
            >
              {loadingOSStatus ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Skeleton className="h-[200px] w-[200px] rounded-full" />
                </div>
              ) : dadosOSStatus ? (
                <GraficoOSPorStatus dados={dadosOSStatus} />
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  {t('common.noData')}
                </div>
              )}
            </Section>

            {isAdmin && (
              <Section
                titulo={t('dashboard.tables.providerPerformance')}
                descricao={t('dashboard.tables.providerPerformanceDescription')}
              >
                {loadingPrestadores ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="h-14" />
                    ))}
                  </div>
                ) : dadosPrestadores && dadosPrestadores.length > 0 ? (
                  <TabelaDesempenhoPrestadores dados={dadosPrestadores} />
                ) : (
                  <div className="py-8 text-center text-muted-foreground text-sm">
                    {t('common.noData')}
                  </div>
                )}
              </Section>
            )}
          </div>
        )}

        {/* Tab: Financeiro */}
        {activeTab === 'financeiro' && isAdmin && (
          <div className="flex flex-col gap-5">
            {loadingMetricasFinanceiras ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-28 rounded-2xl" />
                ))}
              </div>
            ) : (
              metricasFinanceiras && <CardsResumoFinanceiro dados={metricasFinanceiras} />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section
                titulo={t('dashboard.charts.revenue')}
                descricao={t('dashboard.charts.revenueDescription', {
                  period: periodoLabel[periodoSelecionado],
                })}
              >
                {loadingFaturamento ? (
                  <Skeleton className="h-[300px]" />
                ) : faturamentoPorPeriodo ? (
                  <GraficoFaturamento dados={faturamentoPorPeriodo} />
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t('common.noData')}
                  </div>
                )}
              </Section>

              <Section
                titulo={t('dashboard.charts.servicesMix')}
                descricao={t('dashboard.charts.servicesMixDescription')}
              >
                {loadingServicos ? (
                  <div className="h-[300px] flex items-center justify-center">
                    <Skeleton className="h-[200px] w-[200px] rounded-full" />
                  </div>
                ) : servicosMaisVendidos ? (
                  <GraficoServicosPizza dados={servicosMaisVendidos} />
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t('common.noData')}
                  </div>
                )}
              </Section>
            </div>

            <Section
              titulo={t('dashboard.tables.topServices')}
              descricao={t('dashboard.tables.topServicesDescription', {
                period: periodoLabel[periodoSelecionado],
              })}
            >
              {loadingServicos ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : servicosMaisVendidos && servicosMaisVendidos.length > 0 ? (
                <TabelaServicosMaisVendidos dados={servicosMaisVendidos} />
              ) : (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  {t('dashboard.tables.noServicesInPeriod')}
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
